// components/promotions/promoPrice.js
//
// The promotion editor's price arithmetic, on decimal digits rather than
// binary floats: the backend computes the same prices with Python's Decimal
// (api/services/promotion_price_push.py `promo_price`, ROUND_HALF_UP to the
// currency's step), and a float would round 1.005 down where Decimal rounds it
// up — the screen and the store would disagree by a cent.
//
// `digits` is the store currency's decimals (`selectedStore.currency_digits`).

// A wire decimal ('1.2900') or a parsed number as { n: BigInt, scale }, value = n / 10^scale.
function toDecimal(value) {
  const text = String(value);
  const match = /^(\d+)(?:\.(\d+))?$/.exec(text);
  if (!match) throw new Error(`promoPrice: not a plain non-negative decimal: "${text}"`);
  const frac = match[2] ?? '';
  return { n: BigInt(match[1] + frac), scale: frac.length };
}

function roundHalfUp({ n, scale }, digits) {
  if (!Number.isInteger(digits) || digits < 0) {
    throw new Error(`promoPrice: currency digits must be a whole number, got ${digits}`);
  }
  if (scale <= digits) return Number(n) / 10 ** scale;
  const divisor = 10n ** BigInt(scale - digits);
  return Number((n + divisor / 2n) / divisor) / 10 ** digits;
}

/** `price` rounded half-up to the currency's step. */
export function roundMoney(price, digits) {
  return roundHalfUp(toDecimal(price), digits);
}

/** `price` less `pct` percent, rounded half-up to the currency's step. */
export function percentOffPrice(price, pct, digits) {
  const p = toDecimal(price);
  const d = toDecimal(pct);
  // price × (100 − pct) / 100, kept whole: the scales add, plus 2 for the /100.
  const remaining = 100n * 10n ** BigInt(d.scale) - d.n;
  return roundHalfUp({ n: p.n * remaining, scale: p.scale + d.scale + 2 }, digits);
}
